import { Component } from '@theme/component';

/**
 * Klaviyo form custom element that handles email subscription through Klaviyo.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} emailInput
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} successMessage
 * @property {HTMLElement} errorMessage
 * @property {HTMLElement} [errorText]
 *
 * @extends {Component<Refs>}
 */
export class KlaviyoFormComponent extends Component {
	/** @type {number | undefined} */
	#checkInterval = undefined;

	connectedCallback() {
		super.connectedCallback();
		this.#initializeKlaviyo();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		if (this.#checkInterval) {
			clearInterval(this.#checkInterval);
			this.#checkInterval = undefined;
		}
	}

	/**
	 * Initialize Klaviyo integration by checking for the Klaviyo script and attaching to the form.
	 */
	#initializeKlaviyo() {
		const { form } = /** @type {Refs} */ (this.refs);
		if (!form) return;

		// Ensure the form has an ID for Klaviyo to attach to
		if (!form.id) {
			form.id = 'klaviyo-form-' + Math.random().toString(36).substr(2, 9);
		}
		this.#checkInterval = setInterval(() => {
			// @ts-ignore - KlaviyoSubscribe is loaded from external script
			if (typeof window.KlaviyoSubscribe !== 'undefined') {
				clearInterval(this.#checkInterval);
				this.#checkInterval = undefined;
				this.#attachKlaviyo();
			}
		}, 100);
	}

	/**
	 * Attach Klaviyo subscription handling to the form.
	 */
	#attachKlaviyo() {
		const { form, submitButton, successMessage, errorMessage } = /** @type {Refs} */ (this.refs);
		if (!form) return;

		// @ts-ignore - KlaviyoSubscribe is loaded from external script
		window.KlaviyoSubscribe.attachToForms('#' + form.id, {
			custom_success_message: true,
			extra_properties: { $source: 'NewsletterPopup' },
			success: () => {
				if (submitButton) submitButton.classList.remove('loading');
				if (successMessage) {
					successMessage.style.display = 'flex';
					successMessage.focus();
				}
				if (errorMessage) {
					errorMessage.style.display = 'none';
				}
			},
		});

		// Add loading state on form submit
		this.addEventListener('submit', this.#handleSubmit);

		// Handle Klaviyo subscription errors
		this.addEventListener('klaviyo.subscribe.error', this.#handleError);
	}

	/**
	 * Handle form submission by adding loading state to button.
	 */
	#handleSubmit = () => {
		const { submitButton } = /** @type {Refs} */ (this.refs);
		if (submitButton) submitButton.classList.add('loading');
	};

	/**
	 * Handle Klaviyo subscription errors.
	 * @param {Event} event
	 */
	#handleError = (event) => {
		const { submitButton, errorMessage, errorText } = /** @type {Refs} */ (this.refs);

		if (submitButton) submitButton.classList.remove('loading');

		if (errorMessage && errorText) {
			errorText.textContent = 'Subscription failed. Please try again.';
			errorMessage.style.display = 'flex';
			errorMessage.focus();
		}
	};
}

if (!customElements.get('klaviyo-form-component')) {
	customElements.define('klaviyo-form-component', KlaviyoFormComponent);
}
